/**
 * Settings → You → Your skills (YAG-784): a person's own GitHub
 * repository of skills, commands and rules. Wright loads it only on runs this
 * person starts and on their personal Team, never on another Team's run.
 * YAGNI keeps the pointer; each run reads the repository fresh.
 *
 * Save checks that the YAGNI GitHub App can read the repository and answers
 * with the commit it read.
 */
import { useEffect, useState } from "react";
import { parseGitHubRepoInput, type SkillsRepoSetting } from "@yagni/shared";

import { Button } from "../ui/button.js";
import { Input } from "../ui/input.js";
import { ChapterCard, ChapterSection } from "./chapter.js";
import {
  GITHUB_REPO_HINT,
  SKILLS_FAILED,
  SKILLS_HELP,
  SKILLS_INPUT,
  SKILLS_PLACEHOLDER,
  SKILLS_REMOVE,
  SKILLS_REMOVED,
  SKILLS_SAVE,
  SKILLS_SAVED,
  SKILLS_SECTION,
} from "../lib/agentSourcesCopy.js";
import {
  fetchSkillsRepo as defaultFetch,
  removeSkillsRepo as defaultRemove,
  saveSkillsRepo as defaultSave,
  type FetchSkillsRepo,
  type RemoveSkillsRepo,
  type SaveSkillsRepo,
} from "./agentSourcesApi.js";

export interface SkillsRepoSectionProps {
  fetchSkillsRepo?: FetchSkillsRepo;
  saveSkillsRepo?: SaveSkillsRepo;
  removeSkillsRepo?: RemoveSkillsRepo;
}

export function SkillsRepoSection({
  fetchSkillsRepo = defaultFetch,
  saveSkillsRepo = defaultSave,
  removeSkillsRepo = defaultRemove,
}: SkillsRepoSectionProps = {}) {
  // undefined = loading or the read failed: the section stays out of the way.
  const [saved, setSaved] = useState<SkillsRepoSetting | null | undefined>(undefined);
  const [repo, setRepo] = useState("");
  const [busy, setBusy] = useState<null | "save" | "remove">(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    fetchSkillsRepo()
      .then((read) => {
        if (!live || !read || !("skillsRepo" in read)) return;
        setSaved(read.skillsRepo ?? null);
        setRepo(read.skillsRepo?.fullName ?? "");
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [fetchSkillsRepo]);

  if (saved === undefined) return null;

  async function save() {
    setError(null);
    setNotice(null);
    const parsed = parseGitHubRepoInput(repo);
    if (!parsed) {
      setError(GITHUB_REPO_HINT);
      return;
    }
    setBusy("save");
    try {
      // Keep a saved branch when the same repository is saved again.
      const sameRepo = saved && saved.fullName.toLowerCase() === parsed.fullName.toLowerCase();
      const result = await saveSkillsRepo({
        repo: parsed.fullName,
        ...(sameRepo && saved.ref ? { ref: saved.ref } : {}),
      });
      setSaved({ fullName: result.fullName, ref: result.ref });
      setRepo(result.fullName);
      setNotice(SKILLS_SAVED(result.sha));
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : SKILLS_FAILED);
    } finally {
      setBusy(null);
    }
  }

  async function remove() {
    setError(null);
    setNotice(null);
    setBusy("remove");
    try {
      await removeSkillsRepo();
      setSaved(null);
      setRepo("");
      setNotice(SKILLS_REMOVED);
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : SKILLS_FAILED);
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      <ChapterSection label={SKILLS_SECTION} />
      <ChapterCard className="space-y-2 p-4" data-testid="skills-repo-section">
        <label htmlFor="skills-repo" className="block text-sm font-medium text-[var(--text-primary)]">
          {SKILLS_INPUT}
        </label>
        <div className="flex flex-wrap items-center gap-2">
          <Input
            id="skills-repo"
            value={repo}
            placeholder={SKILLS_PLACEHOLDER}
            spellCheck={false}
            className="max-w-sm font-mono"
            onChange={(event) => {
              setRepo(event.target.value);
              setNotice(null);
            }}
          />
          <Button size="sm" disabled={busy !== null || !repo.trim()} loading={busy === "save"} onClick={() => void save()}>
            {SKILLS_SAVE}
          </Button>
          {saved && (
            <Button variant="ghost" size="sm" disabled={busy !== null} loading={busy === "remove"} onClick={() => void remove()}>
              {SKILLS_REMOVE}
            </Button>
          )}
        </div>
        <p className="m-0 text-xs text-[var(--text-tertiary)]">{SKILLS_HELP}</p>
        {error ? (
          <p role="alert" className="m-0 text-sm text-[var(--error)]">
            {error}
          </p>
        ) : notice ? (
          <p role="status" className="m-0 text-sm text-[var(--text-secondary)]">
            {notice}
          </p>
        ) : null}
      </ChapterCard>
    </>
  );
}
